import { buildUniqueSelector } from './selector-utils';

export class FormLabelsRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'formLabels';
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode, pageUrl = '') {
		const controlNodes = this.getFormControlNodes(documentNode);
		const controls = controlNodes.map((controlNode, index) => this.buildControlInfo(controlNode, index, pageUrl));
		const invalidControls = controls.filter((control) => control.isValidLabel === false);

		if (controls.length === 0) {
			return {
				status: 'pass',
				message: this.__( 'No form controls found.', 'a11yfix' ),
				details: {
					controls,
					invalidControls
				}
			};
		}

		if (invalidControls.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'Form controls with missing/invalid labels:', 'a11yfix' )} ${invalidControls.length}`,
				details: {
					controls,
					invalidControls
				}
			};
		}

		return {
			status: 'pass',
			message: this.__( 'All form controls have valid labels.', 'a11yfix' ),
			details: {
				controls,
				invalidControls
			}
		};
	}

	getFormControlNodes(documentNode) {
		const selector = 'input, select, textarea';

		return Array.from(documentNode.querySelectorAll(selector)).filter((node) => {
			if (node.closest('#wpadminbar')) {
				return false;
			}

			if (node.tagName.toLowerCase() !== 'input') {
				return true;
			}

			const type = (node.getAttribute('type') || 'text').toLowerCase();
			const ignoredInputTypes = ['hidden', 'button', 'submit', 'reset', 'image'];

			return !ignoredInputTypes.includes(type);
		});
	}

	buildControlInfo(controlNode, index, pageUrl) {
		const id = controlNode.getAttribute('id') || '';
		const name = controlNode.getAttribute('name') || '';
		const tag = controlNode.tagName.toLowerCase();
		const type = tag === 'input' ? (controlNode.getAttribute('type') || 'text').toLowerCase() : tag;
		const ariaLabel = (controlNode.getAttribute('aria-label') || '').trim();
		const ariaLabelledBy = (controlNode.getAttribute('aria-labelledby') || '').trim();
		const associatedLabelText = this.getAssociatedLabelText(controlNode, id);
		const labelInfo = this.resolveLabelInfo(
			ariaLabel,
			ariaLabelledBy,
			associatedLabelText,
			controlNode.ownerDocument
		);
		const isValidLabel = labelInfo.isValid;
		let issue = '';

		if (!isValidLabel) {
			issue = this.__( 'Missing accessible label (label, aria-label, or aria-labelledby).', 'a11yfix' );
		}

		return {
			index: index + 1,
			url: pageUrl,
			selector: buildUniqueSelector(controlNode),
			tag,
			type,
			id,
			name,
			ariaLabel,
			ariaLabelledBy,
			labelText: associatedLabelText,
			labelSource: labelInfo.source,
			labelValue: labelInfo.value,
			isValidLabel,
			issue
		};
	}

	resolveLabelInfo(ariaLabel, ariaLabelledBy, associatedLabelText, documentNode) {
		if (ariaLabel.length > 0) {
			return {
				isValid: true,
				source: 'aria-label',
				value: ariaLabel
			};
		}

		if (associatedLabelText.length > 0) {
			return {
				isValid: true,
				source: 'label',
				value: associatedLabelText
			};
		}

		if (!ariaLabelledBy || !documentNode) {
			return {
				isValid: false,
				source: '',
				value: ''
			};
		}

		const labelledByIds = ariaLabelledBy.split(/\s+/).filter(Boolean);

		if (labelledByIds.length === 0) {
			return {
				isValid: false,
				source: '',
				value: ''
			};
		}

		const labelledByText = labelledByIds
			.map((labelId) => {
			const labelNode = documentNode.getElementById(labelId);

			if (!labelNode) {
				return '';
			}

			return (labelNode.textContent || '').trim();
		})
			.filter(Boolean)
			.join(' ')
			.trim();

		if (labelledByText.length > 0) {
			return {
				isValid: true,
				source: 'aria-labelledby',
				value: labelledByText
			};
		}

		return {
			isValid: false,
			source: '',
			value: ''
		};
	}

	getAssociatedLabelText(controlNode, controlId) {
		if (controlId) {
			const linkedLabel = controlNode.ownerDocument.querySelector(`label[for="${this.escapeSelector(controlId)}"]`);

			if (linkedLabel) {
				return (linkedLabel.textContent || '').trim();
			}
		}

		const wrappingLabel = controlNode.closest('label');

		if (!wrappingLabel) {
			return '';
		}

		return (wrappingLabel.textContent || '').trim();
	}

	escapeSelector(value) {
		if (window.CSS && typeof window.CSS.escape === 'function') {
			return window.CSS.escape(value);
		}

		return String(value).replace(/"/g, '\\"');
	}
}
